import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import MathText from "@/components/Math";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { CheckCircle2, XCircle, RefreshCw, ArrowLeft, Timer, Sparkles } from "lucide-react";

// Very lightweight normaliser for final answers: strips whitespace, math
// delimiters and common trailing punctuation so that a student's plain input
// like "3" matches a stored final like "$3$."
function normalise(s) {
  if (s == null) return "";
  return String(s)
    .replace(/\$/g, "")
    .replace(/\\\\/g, "")
    .replace(/[.\s]/g, "")
    .toLowerCase();
}

export default function PracticeTest() {
  const { chapterId } = useParams();
  const { user } = useAuth();
  const [test, setTest] = useState(null);
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [running, setRunning] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState(false);

  const load = async () => {
    setSubmitted(false);
    setAnswers({});
    setSeconds(0);
    const { data } = await api.get(`/practice-test/${chapterId}?count=10`);
    setTest(data);
    setRunning(true);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [chapterId]);

  useEffect(() => {
    if (!running) return;
    const id = setInterval(() => setSeconds((s) => s + 1), 1000);
    return () => clearInterval(id);
  }, [running]);

  const submit = async () => {
    setSubmitted(true);
    setRunning(false);
    if (!user || saved) return;
    const scoreVal = test.questions.reduce(
      (n, q) => n + (normalise(answers[q.id]) && normalise(answers[q.id]) === normalise(q.final) ? 1 : 0),
      0
    );
    try {
      await api.post("/practice-attempts", {
        chapter_id: test.chapter_id,
        score: scoreVal,
        total: test.questions.length,
        time_seconds: seconds,
      });
      setSaved(true);
    } catch (e) {
      setSaveError(true);
    }
  };

  if (!test) return <div className="text-zinc-500">Loading test…</div>;

  const score = test.questions.reduce((n, q) => n + (normalise(answers[q.id]) && normalise(answers[q.id]) === normalise(q.final) ? 1 : 0), 0);
  const attempted = test.questions.reduce((n, q) => n + (answers[q.id]?.trim() ? 1 : 0), 0);
  const mm = String(Math.floor(seconds / 60)).padStart(2, "0");
  const ss = String(seconds % 60).padStart(2, "0");

  return (
    <div className="max-w-3xl mx-auto fade-up">
      <Link to={`/chapters/${chapterId}`} className="text-xs text-zinc-500 hover:text-yellow-300 inline-flex items-center gap-1 mb-3">
        <ArrowLeft className="w-3 h-3" /> Back to chapter
      </Link>

      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-pink-300 mb-1">Practice Test · {test.class_label}</div>
          <h1 className="font-display text-3xl font-bold tracking-tight" data-testid="practice-title">{test.chapter_title}</h1>
          <p className="text-zinc-400 text-sm mt-1">10 questions randomised from the chapter. Type each final answer and submit to see your score.</p>
        </div>
        <div className="text-right shrink-0">
          <div className="inline-flex items-center gap-2 rounded-full bg-orange-300/10 border border-orange-300/30 px-3 py-1 font-mono text-yellow-300 text-sm">
            <Timer className="w-3.5 h-3.5" /> {mm}:{ss}
          </div>
          <div className="mt-2 text-[11px] text-zinc-500">{attempted}/{test.questions.length} attempted</div>
        </div>
      </div>

      <div className="space-y-4">
        {test.questions.map((q, i) => {
          const student = answers[q.id] || "";
          const isCorrect = submitted && normalise(student) && normalise(student) === normalise(q.final);
          const isWrong = submitted && normalise(student) && normalise(student) !== normalise(q.final);
          return (
            <div key={q.id} className="rounded-lg border border-white/10 bg-[#152040] p-5" data-testid={`practice-q-${q.id}`}>
              <div className="flex items-start gap-3">
                <span className="font-mono text-yellow-300 text-xs mt-1">{String(i + 1).padStart(2, "0")}</span>
                <div className="flex-1">
                  <div className="font-display text-base font-semibold leading-snug"><MathText>{q.q}</MathText></div>
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <input
                      type="text"
                      value={student}
                      onChange={(e) => setAnswers((a) => ({ ...a, [q.id]: e.target.value }))}
                      disabled={submitted}
                      placeholder="Your final answer"
                      data-testid={`practice-input-${q.id}`}
                      className="flex-1 min-w-[200px] rounded-md bg-[#0b1428] border border-white/10 px-3 py-2 text-sm text-zinc-100 placeholder:text-zinc-600 focus:outline-none focus:border-orange-300/50"
                    />
                    {isCorrect && <CheckCircle2 className="w-5 h-5 text-emerald-400" data-testid={`right-${q.id}`} />}
                    {isWrong && <XCircle className="w-5 h-5 text-rose-400" data-testid={`wrong-${q.id}`} />}
                  </div>
                  {submitted && (
                    <div className="mt-3 text-sm">
                      <div className="text-pink-300"><span className="text-zinc-500">Correct: </span><MathText>{q.final}</MathText></div>
                      <details className="mt-1 text-xs text-zinc-400">
                        <summary className="cursor-pointer hover:text-yellow-300">Show solution</summary>
                        <div className="mt-2 rounded-md border border-white/10 bg-[#0f1a34] p-3 font-mono">
                          {q.solution.map((s, j) => <div key={j}><MathText>{s}</MathText></div>)}
                        </div>
                      </details>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="sticky bottom-4 mt-6">
        {!submitted ? (
          <Button onClick={submit} className="w-full h-12 bg-sky-400 hover:bg-sky-300 text-slate-900 font-semibold rounded-full" data-testid="submit-practice">
            Submit test
          </Button>
        ) : (
          <div className="rounded-xl border border-orange-300/40 bg-gradient-to-br from-pink-300/10 to-transparent p-5 text-center">
            <div className="font-display text-2xl font-bold text-yellow-100">You scored {score}/{test.questions.length}</div>
            <div className="text-sm text-zinc-400 mt-1">
              {score === test.questions.length ? "Full marks! Nailed every question." :
                score >= 7 ? "Great work — a few slips to iron out." :
                score >= 4 ? "Solid attempt. Review the ones you missed." :
                "Take a look at the theory and try again — you've got this."}
            </div>
            {user && saved && (
              <div className="text-xs text-emerald-400 mt-2">✓ Saved to your progress</div>
            )}
            {user && saveError && (
              <div className="text-xs text-rose-400 mt-2">Couldn't save score to your progress — try again in a moment.</div>
            )}
            {!user && (
              <div className="text-xs text-zinc-500 mt-2">Sign in to save your score and track progress</div>
            )}
            <div className="flex gap-2 justify-center mt-4">
              <Button onClick={load} className="rounded-full bg-white/10 hover:bg-white/20 text-white" data-testid="retry-practice">
                <RefreshCw className="w-4 h-4 mr-1" /> New test
              </Button>
              <Link to="/tutor">
                <Button variant="outline" className="rounded-full border-orange-300/40 text-yellow-300 hover:bg-orange-300/10">
                  <Sparkles className="w-4 h-4 mr-1" /> Ask AI Tutor
                </Button>
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
